/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ColumnFiltersState,
  ExpandedState,
  RowSelectionState,
  SortingState,
  TableOptions,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  getGroupedRowModel,
  GroupingState
} from "@tanstack/react-table";
import React, { FC, Fragment, useEffect, useState } from "react";
import Input from "./Input";
import Spinner from "./Spinner";
import { cn } from "../utils/utils";
import arrowDownIcon from "../assets/arrow-down.svg";
import { ClientPagination } from "./ClientPagination";
import PaginationComponent from "./Pagination";
import { Layers2 } from "lucide-react";

interface TableProps {
  columns: any[];
  data: any[];
  onRowClick?: (row: any) => void;
  title?: string;
  subtitle?: string;
  isPaginating?: boolean;
  isGlobalSearch?: boolean;
  searchPlaceholder?: string;
  showheader?: boolean;
  renderSubComponent?: (props: any) => React.ReactElement;
  headClass?: string;
  getRowCanExpand?: (row: any) => boolean;
  loading?: boolean;
  onPaginationChange?: any;
  pagination?: any;
  manualPagination?: boolean;
  pageCount?: number;
  onGlobalSearch?: (value: string) => void;
  getSelectedRows?: any;
  isLoading?: boolean;
  showSearch?: boolean;
  manualSorting?: boolean;
  sorting?: any;
  onSortingChange?: any;
  searchQuery?: string;
  getTableInstance?: any;
  rowHighlight?: (row: any) => boolean;
  groupingColumns?: string[];
}

const Table: FC<TableProps> = ({
  columns,
  data,
  onRowClick,
  title,
  subtitle,
  isPaginating = true,
  isGlobalSearch = true,
  searchPlaceholder,
  showheader = true,
  renderSubComponent,
  headClass,
  getRowCanExpand = row => row.subRows?.length > 0,
  isLoading = false,
  onPaginationChange = () => { },
  pageCount,
  pagination,
  manualPagination = false,
  onGlobalSearch,
  getSelectedRows,
  showSearch = true,
  manualSorting = false,
  onSortingChange,
  sorting,
  searchQuery,
  getTableInstance,
  rowHighlight = () => false,
  groupingColumns = []
}) => {
  const [columnFilters, setColumnsFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [grouping, setGrouping] = useState<GroupingState>(groupingColumns);

  const reactTableProps: TableOptions<any> = {
    data,
    columns,
    enableRowSelection: true,
    manualPagination: manualPagination,
    manualSorting: manualSorting,
    state: manualPagination
      ? {
        pagination,
        columnFilters,
        rowSelection,
        expanded,
        globalFilter: searchQuery || globalFilter,
        sorting: manualSorting ? sorting : internalSorting,
        grouping,
      }
      : {
        columnFilters,
        rowSelection,
        expanded,
        globalFilter: searchQuery || globalFilter,
        sorting: manualSorting ? sorting : internalSorting,
        grouping,
      },
    getRowCanExpand,
    onSortingChange: manualSorting ? onSortingChange : setInternalSorting,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: isPaginating ? getPaginationRowModel() : undefined,
    onColumnFiltersChange: setColumnsFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getGroupedRowModel: getGroupedRowModel(),
    onGroupingChange: setGrouping,
  };

  if (manualPagination) {
    (reactTableProps["onPaginationChange"] = onPaginationChange), (reactTableProps["pageCount"] = pageCount);
  }

  const table = useReactTable(reactTableProps);

  const filterHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    table.getColumn("item_code")?.setFilterValue(e.target.value);
  };

  const globalFilterHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onGlobalSearch) setGlobalFilter(String(e.target.value));
    else onGlobalSearch(String(e.target.value));
  };

  useEffect(() => {
    if (getSelectedRows) getSelectedRows(table.getSelectedRowModel().rows);
  }, [table.getSelectedRowModel()]);

  useEffect(() => {
    if (getTableInstance) {
      getTableInstance(table);
    }
  }, [table]);

  useEffect(() => {
    if (!groupingColumns) return;

    setGrouping(prev => {
      const same =
        prev.length === groupingColumns.length &&
        prev.every((v, i) => v === groupingColumns[i]);

      return same ? prev : groupingColumns;
    });
  }, [groupingColumns]);

  return (
    <>
      {title && (
        <section className="flex items-center justify-between mt-5 mb-6">
          <div>
            <h4 className="text-2xl font-semibold text-[#49484C]">{title}</h4>
            <p className="text-base font-medium text-[#49484C] opacity-60">{subtitle}</p>
          </div>
          {showSearch && (
            <div className="inline-flex items-center gap-2">
              <Input
                type="input"
                value={globalFilter}
                placeholder={searchPlaceholder || "Search..."}
                onChange={isGlobalSearch ? globalFilterHandler : filterHandler}
                className="px-3  rounded-lg text-base text-[#1B1A1F]"
                showIcon={true}
              />
            </div>
          )}
        </section>
      )}

      {isLoading && (
        <div className="flex justify-center w-full py-5">
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <>
          {" "}
          <div className="relative z-0 bg-white  border rounded-[0.625rem] min-w-[600px] overflow-auto">
            <table className="w-full text-sm text-left text-gray-500 table-auto rounded-[0.625rem]">
              {showheader && (
                <thead className={cn(`pt-4 w-full ${headClass ? headClass : ""}`)}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr className="border-b divide-gray-200" key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          style={{ width: header.column.getSize() || "auto" }}
                          className={`py-2 px-3 text-left text-sm font-normal overflow-hidden text-[0,0,0,0.4] `} //Display Issue
                          key={header.id}
                        >
                          <div className="flex items-center max-w-full gap-2 overflow-hidden">
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

                            {header.column.getCanSort() && (
                              <button onClick={header.column.getToggleSortingHandler()}>
                                <img src={arrowDownIcon} alt="sorting" />
                              </button>
                            )}
                            {{
                              asc: " 🔼",
                              desc: " 🔽",
                            }[header.column.getIsSorted() as string] ?? null}

                            {groupingColumns.includes(header.column.id) && (
                              <button
                                onClick={() => {
                                  header.column.toggleGrouping();
                                }}
                                title={`${header.column.getIsGrouped() ? "Ungroup" : "Group"} by ${header.column.columnDef.header}`}
                                className={`transition-colors ${header.column.getIsGrouped() ? "text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Layers2 size={16} />
                              </button>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
              )}

              {data?.length === 0 && (
                <td colSpan={table.getAllColumns().length}>
                  <p className="mt-10 mb-10 text-3xl text-center">No data...</p>
                </td>
              )}

              {data && data.length > 0 && (
                <tbody className="bg-white divide-y divide-gray-200 text-[#49484C]">
                  {table.getRowModel().rows.map((row) => (
                    <Fragment key={row.id}>
                      {/* M10-11991 */}
                      <tr
                        className={`${onRowClick ? "hover:bg-gray-200 cursor-pointer" : ""} ${rowHighlight(row) ? "bg-red-50" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onRowClick) onRowClick(row);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={`${cell.column.id}_${row.index}`}
                            className="px-3 py-2 overflow-hidden text-sm font-semibold whitespace-nowrap" //Display Issue
                            style={cell.column.columnDef.size !== undefined ? { maxWidth: cell.column.getSize() } : {}} //Display Issue
                          >
                            <div className="flex items-center gap-1">
                              {/* Grouping / Aggregation / Placeholder handling for nested groups */}
                              {cell.getIsGrouped() ? (
                                <div
                                  className="flex items-center gap-1"
                                  style={{ paddingLeft: `${row.depth * 20}px` }}
                                >
                                  {row.getCanExpand() && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        row.toggleExpanded();
                                      }}
                                      className="mr-2 text-sm"
                                    >
                                      {row.getIsExpanded() ? "▾" : "▸"}
                                    </button>
                                  )}

                                  <span className="font-semibold">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </span>

                                  <span className="text-xs opacity-60 ml-2">
                                    ({row.subRows.length})
                                  </span>
                                </div>
                              ) : cell.getIsAggregated() ? (
                                flexRender(
                                  cell.column.columnDef.aggregatedCell ??
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              ) : cell.getIsPlaceholder() ? null : (
                                flexRender(cell.column.columnDef.cell, cell.getContext())
                              )}

                              {rowHighlight(row) && cell.column.id === "Priceldentifier_Id" && (
                                <svg
                                  className="w-4 h-4 text-red-600 flex-shrink-0"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                      {row.getIsExpanded() && renderSubComponent && ( 
                        <tr> 
                          {/* 2nd row is a custom 1 cell row */}
                          <td colSpan={row.getVisibleCells().length}>{renderSubComponent({ row })}</td>
                        </tr>
                      )} 
                    </Fragment>
                  ))}
                </tbody>
              )}
              <tfoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <th key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}</th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
          {isPaginating && !manualPagination && <ClientPagination table={table} />}
          {isPaginating && manualPagination ? <PaginationComponent table={table} /> : null}
        </>
      )}
    </>
  );
};

export default Table;
